import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

// Thin wrappers around Admin::Api::MergesController. The admin merges API is
// token-authenticated (see Admin::Api::ApiController), so browser calls carry the
// same authenticatedHeadersHash() the profile-conversion flow uses.

const BASE = "/admin/api/merges";

// Job execution states that mean "keep polling", absent a surfaced error_type or
// a stale flag. A committed receipt no longer reaches here at all: the server
// reports success for it, which settles above. The two post-commit hook states
// stay in the list only for the deploy window, when a browser running this
// bundle can still poll a server old enough to answer a receipt with
// success:false; without them such a body would read as an unexpected envelope
// and show a false failure for a merge that succeeded.
export const IN_PROGRESS_JOB_STATES = ["queued", "running", "retry_wait", "hooks_pending", "hooks_running"];

// Which structured error_types the operator can act on with an in-console Retry
// (a plain re-POST of the same type/source/destination — the server decides
// whether that re-dispatches the job or replays hooks). merge_divergence and the
// pre-flight refusals will not succeed unchanged, so Retry is withheld there.
const ERROR_RETRYABLE = {
  v1_unavailable: true,
  dispatch_failed: true,
  deadline: true,
  operation_failed: true,
  post_commit_incomplete: true,
  merge_refused: false,
  invalid_request: false,
  merge_divergence: false,
};

// GET /admin/api/merges/preview — returns { source, destination, type, v1_merge_enabled }.
export async function fetchPreview({ type, sourceId, destinationId }) {
  const params = new URLSearchParams({
    type,
    source_id: sourceId,
    destination_id: destinationId,
  });

  const res = await fetch(`${BASE}/preview?${params}`, { headers: authenticatedHeadersHash() });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || `Preview failed (${res.status})`);
  }
  return data;
}

// GET /admin/api/merges/duplicates — returns { type, duplicates: [...] }.
export async function fetchDuplicates({ type }) {
  const params = new URLSearchParams({ type });

  const res = await fetch(`${BASE}/duplicates?${params}`, { headers: authenticatedHeadersHash() });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || `Finding duplicates failed (${res.status})`);
  }
  return data.duplicates || [];
}

// GET /admin/api/merges/search — typeahead lookup for the entity pickers. Returns
// { results: [{ id, name, slug, self_expert }] }.
export async function searchEntities({ type, q }) {
  const params = new URLSearchParams({ type, q });

  const res = await fetch(`${BASE}/search?${params}`, { headers: authenticatedHeadersHash() });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || `Search failed (${res.status})`);
  }
  return data.results || [];
}

// POST /admin/api/merges — dispatches the merge and waits briefly server-side.
// Resolves with the parsed response body regardless of HTTP status so the caller
// runs it through normalizeMergeArrival(); rejects only on network/parse failure.
export async function postMerge({ type, sourceId, destinationId }) {
  const res = await fetch(BASE, {
    method: "POST",
    headers: {
      ...authenticatedHeadersHash(),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ type, source_id: sourceId, destination_id: destinationId }),
  });

  return res.json().catch(() => ({}));
}

// GET the poll_url handed back by a 202 POST. Always HTTP 200; the body carries
// the same success/error core plus protocol_status/job_state/stale/timestamps.
export async function pollMerge(pollUrl) {
  const res = await fetch(pollUrl, { headers: authenticatedHeadersHash() });
  return res.json().catch(() => ({}));
}

// The single normalizer every UI path funnels through. It consumes all three
// arrival shapes — an immediate POST outcome, a POST 202 dispatch receipt, and a
// GET poll body — and returns one canonical object the components render:
//
//   { phase: "success", ok: true, result, alreadyMerged }
//   { phase: "pending", ok: false, pollUrl, intentId, jobState, protocolStatus }
//   { phase: "error",   ok: false, error, errorType, attention, stale, canRetry }
//
// Classification is by body content (success / error_type / stale / job_state),
// never by HTTP status — the GET endpoint is always 200 and the POST status only
// mirrors what these fields already say.
export function normalizeMergeArrival(rawBody) {
  const body = rawBody || {};
  const protocolStatus = body.protocol_status ?? body.status ?? null;
  const intentId = body.intent_id ?? null;

  // Terminal success: a committed receipt. Its post-commit hooks may still be
  // refreshing caches. The result may carry already_merged:true on an idempotent
  // re-POST.
  if (body.success === true) {
    const result = body.result || {};
    return {
      phase: "success",
      ok: true,
      result,
      alreadyMerged: result.already_merged === true,
      protocolStatus,
      intentId,
    };
  }

  // Terminal/attention failure with a structured type is classified BEFORE staleness, so
  // a structured error always wins: a started+needs_attention divergence keeps its
  // non-retryable divergence handling even past the stale threshold, never degrading into
  // a generic retryable "stale". (The server likewise reports stale=false whenever a
  // structured error is surfaced, so the two agree; this ordering is the belt-and-braces.)
  if (body.error_type) {
    const errorType = body.error_type;
    let attention = null;
    if (errorType === "post_commit_incomplete") attention = "hooks";
    else if (errorType === "merge_divergence") attention = "divergence";
    return {
      phase: "error",
      ok: false,
      error: body.error || null,
      errorType,
      attention,
      stale: body.stale === true,
      canRetry: ERROR_RETRYABLE[errorType] ?? true,
      jobState: body.job_state ?? null,
      protocolStatus,
      intentId,
    };
  }

  // A running/hooks execution the poller found wedged (no state movement past the
  // stale threshold) with NO structured error. Surface a re-dispatch affordance.
  if (body.stale === true) {
    return {
      phase: "error",
      ok: false,
      error: body.error || null,
      errorType: null,
      attention: null,
      stale: true,
      canRetry: true,
      jobState: body.job_state ?? null,
      protocolStatus,
      intentId,
    };
  }

  // Still in flight — a 202 dispatch receipt (poll_url present) or a poll body
  // reporting an in-progress job_state. Keep polling.
  const inProgress = body.poll_url || IN_PROGRESS_JOB_STATES.includes(body.job_state);
  if (inProgress) {
    return {
      phase: "pending",
      ok: false,
      pollUrl: body.poll_url || null,
      intentId,
      jobState: body.job_state ?? null,
      protocolStatus,
    };
  }

  // Anything else is an unexpected envelope — treat as a retryable generic error.
  return {
    phase: "error",
    ok: false,
    error: body.error || "Unexpected merge response.",
    errorType: null,
    attention: null,
    stale: false,
    canRetry: true,
    jobState: body.job_state ?? null,
    protocolStatus,
    intentId,
  };
}
